import { createContext, useContext, useEffect, useState } from "react";
import {
    getNotificacionesNoLeidas,
    getNotificacionesLeidas,
    marcarNotificacionLeida,
} from "../../api/apiResources";

// Cada cuanto le volvemos a preguntar al backend si hay notificaciones nuevas.
// HTTP es request/response: el servidor no puede avisarnos solo, y la notificacion
// la genera otro usuario, asi que el cliente tiene que preguntar cada tanto.
const POLL_MS = 30000;

const NotificacionesContext = createContext(null);

export const useNotificaciones = () => useContext(NotificacionesContext);

export const NotificacionesProvider = ({ user, children }) => {
    const usuarioId = user?.id;

    const [noLeidas, setNoLeidas] = useState([]);
    const [leidas, setLeidas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState("");

    // Carga inicial + refresco periodico del contador.
    // La funcion se define adentro del efecto, asi no hace falta memoizarla
    // ni ponerla en el array de dependencias.
    useEffect(() => {
        if (!usuarioId) return;

        const cargarNoLeidas = async () => {
            try {
                const data = await getNotificacionesNoLeidas(usuarioId);
                setNoLeidas(data);
            } catch (e) {
                // El contador se reintenta en el proximo ciclo, no molestamos al usuario.
            }
        };

        cargarNoLeidas();
        const intervalo = setInterval(cargarNoLeidas, POLL_MS);

        // Cleanup: se ejecuta al desmontar o si cambia el usuario. Evita fugas de memoria.
        return () => clearInterval(intervalo);
    }, [usuarioId]);

    // Estas funciones se invocan desde event handlers (nunca desde un array de
    // dependencias), por eso no necesitan useCallback.
    const refrescar = async () => {
        if (!usuarioId) return;
        try {
            setNoLeidas(await getNotificacionesNoLeidas(usuarioId));
        } catch (e) {
            setError("No se pudieron cargar las notificaciones.");
        }
    };

    const cargarTodo = async () => {
        if (!usuarioId) return;
        setCargando(true);
        setError("");
        try {
            const [sinLeer, yaLeidas] = await Promise.all([
                getNotificacionesNoLeidas(usuarioId),
                getNotificacionesLeidas(usuarioId),
            ]);
            setNoLeidas(sinLeer);
            setLeidas(yaLeidas);
        } catch (e) {
            setError("No se pudieron cargar las notificaciones.");
        } finally {
            setCargando(false);
        }
    };

    const marcarLeida = async (notificacionId) => {
        if (!notificacionId) return;
        try {
            await marcarNotificacionLeida(notificacionId);
            await cargarTodo();
        } catch (e) {
            setError("No se pudo marcar la notificación como leída.");
        }
    };

    const value = {
        usuarioId,
        noLeidas,
        leidas,
        cargando,
        error,
        cargarTodo,
        refrescar,
        marcarLeida,
    };

    return (
        <NotificacionesContext.Provider value={value}>
            {children}
        </NotificacionesContext.Provider>
    );
};
